import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Broom, Warning, PencilSimple, ArrowSquareOut, MagnifyingGlass, CheckCircle, Wrench } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import useAuthStore from '../store/authStore';
import { confirm } from '../components/ConfirmDialog';

import { notify } from '../lib/notify';
import { InlineSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * President-only Data Quality report — iter80.
 *
 * Lists every lead in the tenant with at least one `value_flags[]` entry
 * (e.g. Dr Venu's `12577795.466` fractional lead_value, or a Won lead whose
 * booked value is far below the SE's original forecast). Provides:
 *   - Inline correction: edit `lead_value` in-place and save via
 *     `PATCH /api/leads/{id}/value` (no full-form round-trip).
 *   - "Open lead" shortcut to the full LeadDetail page when a deeper fix
 *     is needed.
 *   - A search box so the President can jump straight to a specific
 *     customer/lead name in a long list.
 */
const FLAG_LABELS = {
  fractional_lead_value: {
    label: 'Fractional lead value',
    tone: 'amber',
    desc: 'Non-integer value (paise beyond 2 decimals) — usually a paste/typo.',
  },
  booked_far_below_forecast: {
    label: 'Booked ≪ forecast',
    tone: 'rose',
    desc: 'Won lead where booked value is under 20% of the SE\'s original forecast.',
  },
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}`;

export default function DataQuality() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const tp = useTenantPath();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/data-quality/leads`);
      setItems(r.data.items || []);
    } catch (e) {
      console.error(e);
      notify(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((l) =>
      (l.name || '').toLowerCase().includes(q) ||
      (l.company || '').toLowerCase().includes(q) ||
      (l.owner_name || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const startEdit = (l) => {
    setEditingId(l.id);
    // Suggest the rounded whole-rupee value for fractional flags.
    if ((l.value_flags || []).includes('fractional_lead_value')) {
      setDraftValue(String(Math.round(Number(l.lead_value || 0))));
    } else {
      setDraftValue(String(l.lead_value || 0));
    }
  };
  const cancelEdit = () => { setEditingId(null); setDraftValue(''); };

  // iter82 — recompute transport-GST across every open-lead quote (President-only).
  const [recomputing, setRecomputing] = useState(false);
  const handleRecomputeTransportGst = async () => {
    const ok = await confirm({
      title: 'Recompute transport GST?',
      body: 'Adds 18% GST on transportation for every quotation whose lead is NOT already Won / Completed / Lost. Historical won/lost quotes stay untouched (their invoices are already sent to customers). Idempotent — already-correct quotes are skipped.',
      confirmText: 'Recompute',
    });
    if (!ok) return;
    setRecomputing(true);
    try {
      const r = await axios.post(`${API}/admin/quotations/recompute-transport-gst`);
      notify(r.data.message || 'Done');
    } catch (e) {
      notify(e.response?.data?.detail || 'Recompute failed');
    } finally {
      setRecomputing(false);
    }
  };

  const saveEdit = async (l) => {
    const num = parseFloat(draftValue);
    if (Number.isNaN(num) || num < 0) {
      notify('Enter a valid non-negative number.');
      return;
    }
    setSavingId(l.id);
    try {
      await axios.patch(`${API}/leads/${l.id}/value`, { lead_value: num });
      setEditingId(null);
      setDraftValue('');
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  if (user?.role !== 'President') {
    return (
      <div className="p-6" data-testid="data-quality-forbidden">
        <div className="medical-card p-6 text-center text-[#6D8E87]">
          Data Quality is a President-only report.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="data-quality-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
          <Broom size={28} weight="bold" />
        </div>
        <div className="flex-1">
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
            Data Quality
          </h1>
          <p className="text-[#6D8E87]">
            Leads with suspicious values (fractional rupees, wide forecast-vs-booked gaps). Clean up here to keep dashboards accurate.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRecomputeTransportGst}
          disabled={recomputing}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-800 text-xs font-semibold hover:bg-indigo-100 disabled:opacity-60"
          title="One-shot recompute of 18% GST on transportation for every quote whose lead is still open."
          data-testid="dq-recompute-transport-gst-btn"
        >
          <Wrench size={14} weight="bold" />
          {recomputing ? 'Recomputing…' : 'Recompute transport GST'}
        </button>
      </div>

      {/* Legend */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="data-quality-legend">
        {Object.entries(FLAG_LABELS).map(([k, v]) => (
          <span
            key={k}
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full ${
              v.tone === 'amber' ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : 'bg-rose-50 text-rose-700 border border-rose-200'
            }`}
            title={v.desc}
          >
            <Warning size={12} weight="fill" />
            {v.label}
          </span>
        ))}
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <MagnifyingGlass
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D8E87]"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by lead, company, or owner…"
          className="w-full pl-9 pr-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30"
          data-testid="data-quality-search"
        />
      </div>

      {/* Table */}
      <div className="medical-card overflow-hidden" data-testid="data-quality-table">
        {loading ? (
          <InlineSkeleton />
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center" data-testid="data-quality-empty">
            <CheckCircle size={40} weight="fill" className="text-emerald-500 mx-auto mb-2" />
            <div className="text-[#052A23] font-semibold">
              {items.length === 0 ? 'All clean!' : 'No matches for that search.'}
            </div>
            <div className="text-xs text-[#6D8E87] mt-1">
              {items.length === 0 ? 'No flagged leads in this tenant.' : 'Try a different query.'}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F9F7] text-[10px] uppercase tracking-widest text-[#6D8E87]">
                <tr>
                  <th className="text-left px-4 py-3">Lead</th>
                  <th className="text-left px-4 py-3">Owner</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Lead Value (stored)</th>
                  <th className="text-right px-4 py-3">Booked (net)</th>
                  <th className="text-left px-4 py-3">Flags</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => {
                  const isEditing = editingId === l.id;
                  return (
                    <tr
                      key={l.id}
                      className="border-t border-[#E2ECEA] hover:bg-[#FAFDFC]"
                      data-testid={`data-quality-row-${l.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#052A23]">{l.name || '—'}</div>
                        <div className="text-[11px] text-[#6D8E87]">{l.company || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-[#3B5A54]">{l.owner_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          l.status === 'won' ? 'bg-emerald-100 text-emerald-700'
                          : l.status === 'lost' ? 'bg-rose-100 text-rose-700'
                          : l.status === 'completed' ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                        }`}>{(l.status || '').replace(/_/g, ' ').toUpperCase()}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            step="1"
                            value={draftValue}
                            onChange={(e) => setDraftValue(e.target.value)}
                            className="w-36 text-right px-2 py-1 border border-[#00a69c] rounded text-sm"
                            data-testid={`data-quality-input-${l.id}`}
                            autoFocus
                          />
                        ) : (
                          <span className="font-mono text-[#052A23]">{inr(l.lead_value)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {l.booked_value_net != null
                          ? <span className="font-mono text-emerald-700">{inr(l.booked_value_net)}</span>
                          : <span className="text-[#6D8E87]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(l.value_flags || []).map((f) => {
                            const meta = FLAG_LABELS[f] || { label: f, tone: 'amber' };
                            return (
                              <span
                                key={f}
                                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                  meta.tone === 'rose' ? 'bg-rose-50 text-rose-700'
                                  : 'bg-amber-50 text-amber-700'
                                }`}
                                title={meta.desc}
                              >
                                <Warning size={10} weight="fill" />
                                {meta.label}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {isEditing ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => saveEdit(l)}
                              disabled={savingId === l.id}
                              className="px-2 py-1 text-xs font-semibold rounded bg-[#00a69c] text-white hover:bg-[#00bfb3] disabled:opacity-60"
                              data-testid={`data-quality-save-${l.id}`}
                            >
                              {savingId === l.id ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="px-2 py-1 text-xs rounded border border-[#D5E8E4] text-[#3B5A54] hover:bg-[#F4F9F7]"
                              data-testid={`data-quality-cancel-${l.id}`}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => startEdit(l)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[#00a69c] text-[#00a69c] hover:bg-[#E2F3F0]"
                              title="Edit lead value in place"
                              data-testid={`data-quality-edit-${l.id}`}
                            >
                              <PencilSimple size={12} weight="bold" />
                              Fix
                            </button>
                            <button
                              type="button"
                              onClick={() => navigate(tp(`/leads/${l.id}`))}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded text-[#3B5A54] hover:bg-[#F4F9F7]"
                              title="Open lead detail"
                              data-testid={`data-quality-open-${l.id}`}
                            >
                              <ArrowSquareOut size={12} weight="bold" />
                              Open
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-3 text-xs text-[#6D8E87]" data-testid="data-quality-count">
        {loading ? '' : `${filtered.length} of ${items.length} flagged lead${items.length === 1 ? '' : 's'}`}
      </div>
    </div>
  );
}
